/**
 * Per-user settings kept on the Frappe user itself — the company and the mailbox, when Clerk is
 * not there to hold them (Akhil, 2026-09-24).
 *
 * `User` has a `defaults` child table of `DefaultValue` rows: Frappe's own per-user slot, which
 * Desk already uses for things like `time_zone`. No new doctype, no custom field, nothing for the
 * Frappe team to add — measured working against rfqdev the same day.
 *
 * Two measured behaviours this file exists to respect:
 *
 *   - the child table is REPLACED by what is sent, exactly as `update_enquiry`'s items are: a row
 *     sent with its `name` is updated, one without is created, and a row left out is DELETED.
 *     Writing one key alone silently dropped the other (measured), so every row travels with
 *     every change — `defaultsRowsFor`.
 *   - `time_zone` is the one row that survives a partial write: Frappe re-adds it on save.
 *
 * Both calls are best effort: a setting that cannot be remembered must never break the screen
 * that set it — the session keeps the value either way, only the memory across refreshes is lost.
 *
 * Only used under the `frappe` auth provider. Clerk deployments keep using the Clerk user's
 * metadata, and the proxy's allowlist deliberately has no `User` write in it.
 */
export const DEFAULTS_PARENT = 'User'

export interface DefaultRow {
  name?: string
  defkey: string
  defvalue: string
}

type Fetcher = (url: string, init: RequestInit) => Promise<{ status: number; json: () => Promise<unknown> }>

/**
 * The WHOLE table to send: every row the user has, with `key` set to `value`.
 *
 * Measured the hard way (2026-09-24): writing `bosun_company` and then `bosun_mailbox` as
 * single-row payloads left the user with only the mailbox. The child table is REPLACED by what is
 * sent, so a partial payload deletes the rest — the one exception being `time_zone`, which Frappe
 * re-adds on save. Rows keep their `name` so they are updated rather than duplicated.
 */
export function defaultsRowsFor(rows: readonly DefaultRow[], key: string, value: string): DefaultRow[] {
  const kept = rows.map((r) => (r.defkey === key ? { ...r, defvalue: value } : { ...r }))
  return kept.some((r) => r.defkey === key) ? kept : [...kept, { defkey: key, defvalue: value }]
}

function rowsUrl(user: string): string {
  const filters = encodeURIComponent(JSON.stringify([['parent', '=', user]]))
  const fields = encodeURIComponent(JSON.stringify(['name', 'defkey', 'defvalue']))
  return `/api/method/frappe.client.get_list?doctype=DefaultValue&parent=${DEFAULTS_PARENT}&filters=${filters}&fields=${fields}&limit_page_length=0`
}

async function readRows(user: string, fetcher: Fetcher): Promise<DefaultRow[]> {
  const response = await fetcher(rowsUrl(user), { credentials: 'include' })
  if (response.status < 200 || response.status >= 300) return []
  const body = (await response.json().catch(() => null)) as { message?: unknown } | null
  return Array.isArray(body?.message) ? (body.message as DefaultRow[]) : []
}

/** The stored value, or `''` — for "not set" and for "could not be read" alike. */
export async function readUserDefault(user: string, key: string, fetcher: Fetcher = fetch as unknown as Fetcher): Promise<string> {
  try {
    const row = (await readRows(user, fetcher)).find((r) => r.defkey === key)
    return typeof row?.defvalue === 'string' ? row.defvalue : ''
  } catch {
    return ''
  }
}

export async function writeUserDefault(user: string, key: string, value: string, fetcher: Fetcher = fetch as unknown as Fetcher): Promise<void> {
  try {
    const rows = await readRows(user, fetcher)
    await fetcher(`/api/resource/${DEFAULTS_PARENT}/${encodeURIComponent(user)}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ defaults: defaultsRowsFor(rows, key, value) }),
    })
  } catch {
    // Best effort — see the header.
  }
}
